<template>
  <div class="w-full p-1 py-3 overflow-y-auto rounded-lg bg-eblack-100/70">
    <div class="flex flex-col w-full gap-4">

      <!-- Photo requirement error (client-side or API-originated) -->
      <div
        v-if="errors.length > 0 && errors.find((e) => e.photo_storage_ids)"
        class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
      >
        {{ errors.find((e) => e.photo_storage_ids).photo_storage_ids }}
      </div>

      <!-- Edit Mode - Show Upload Interface -->
      <div v-if="!showDetails">
        <!-- Existing Photos Preview -->
        <div v-if="mediaItems.length > 0" class="mb-4">
          <h4 class="text-sm font-medium text-gray-700 mb-2">{{ $t("services.auto.fotos_actuales") }}</h4>
          <div class="flex flex-wrap gap-4">
            <div
              v-for="(photo, index) in mediaItems"
              :key="photo.id"
              class="relative"
            >
              <img
                v-if="photo.photoUrl"
                class="h-32 max-w-sm rounded-lg object-cover"
                :src="photo.photoUrl"
                :alt="`Service photo ${index + 1}`"
                @error="handlePhotoError(photo)"
              />
              <div
                v-else
                class="flex h-32 w-32 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500"
              >{{ $t("services.auto.foto_cargada") }}</div>
              <button
                type="button"
                class="absolute top-1 right-1 rounded-full bg-red-500/80 p-1 hover:bg-red-600/80"
                @click="removeMediaItem(index)"
                :disabled="isUploading"
              >
                <IconCircleX class="w-4 h-4 text-white" />
              </button>

              <!-- URL Expiration overlay -->
              <div
                v-if="photo.expired"
                class="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center"
              >
                <button
                  @click="refreshPhotoUrls"
                  class="bg-yellow-500 text-white px-2 py-1 rounded text-xs hover:bg-yellow-600"
                  :disabled="isRefreshing"
                >
                  {{
                    isRefreshing
                      ? $t("services.media.refreshing")
                      : $t("services.media.refresh")
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload Dropzone -->
        <FileUploadDropzone
          folder="services"
          :multiple="true"
          :max-files="maxMediaItems"
          :reserved-file-count="mediaItems.length"
          :upload-metadata="catalogMediaUploadMetadata"
          :max-file-size="5 * 1024 * 1024"
          :allowed-types="['image/jpeg', 'image/jpg', 'image/png']"
          :auto-upload="true"
          :disabled="isUploading || !canAddMore()"
          @upload-complete="onUploadComplete"
          @upload-error="onUploadError"
          @upload-start="onUploadStart"
          @upload-progress="onUploadProgress"
          @files-changed="onFilesChanged"
          ref="dropzone"
        />

        <!-- File Limits Message -->
        <div v-if="totalPhotoCount >= maxMediaItems" class="text-sm text-red-600 mt-2">{{ $t("services.auto.limite_maximo_alcanzado") }}</div>

        <!-- Global Upload Progress -->
        <div v-if="isUploading && uploadProgress" class="mt-4">
          <div class="bg-gray-200 rounded-full h-2">
            <div
              class="bg-blue-600 h-2 rounded-full transition-all duration-300"
              :style="`width: ${uploadProgress.overall || 0}%`"
            ></div>
          </div>
          <p class="text-sm text-gray-600 mt-1">
            Subiendo {{ uploadProgress.current || 0 }} de {{ uploadProgress.total || 0 }} fotos...
          </p>
        </div>

        <!-- Upload Errors -->
        <div v-if="uploadErrors.length > 0" class="mt-4 space-y-2">
          <div
            v-for="(error, index) in uploadErrors"
            :key="index"
            class="p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <div class="flex items-center">
              <svg class="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm text-red-700">{{ error }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Read-only Mode - Show Existing Photos -->
      <div v-else>
        <div v-if="mediaItems.length === 0" class="text-center py-8">
          <p class="text-gray-500">{{ $t("services.auto.no_se_han_agregado_fotos") }}</p>
        </div>

        <div v-else class="flex flex-wrap gap-4">
          <div
            v-for="(photo, index) in mediaItems"
            :key="`readonly-${photo.id}`"
            class="relative"
          >
            <img
              v-if="photo.photoUrl"
              class="h-32 max-w-sm rounded-lg object-cover"
              :src="photo.photoUrl"
              :alt="`Service photo ${index + 1}`"
              @error="handlePhotoError(photo, index)"
            />
            <div
              v-else
              class="flex h-32 w-32 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500"
            >{{ $t("services.auto.foto_cargada") }}</div>

            <!-- URL Expiration overlay for read-only mode -->
            <div
              v-if="photo.expired"
              class="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center"
            >
              <div class="text-center text-white">
                <svg class="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5l-6.928-12c-.77-.833-1.732-.833-2.5 0l-6.928 12c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p class="text-xs">{{ $t("services.auto.imagen_expirada") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { i18n } from "@/i18n";
import { IconCircleX } from '@tabler/icons-vue';
import FileUploadDropzone from '@/components/ui/FileUploadDropzone.vue';
import { useToast } from 'vue-toastification';
import {
  SERVICE_MEDIA_MAX_ITEMS,
  buildServicePhotosChangedPayload,
  getServicePhotoStorageIds,
  mergeServiceMediaItems,
  normalizeServiceMediaItems,
  normalizeUploadedMediaItems,
  revokeServiceMediaPreview,
  revokeServiceMediaPreviews,
} from '@domains/service/models/service-media';

const SERVICE_MEDIA_UPLOAD_METADATA = Object.freeze({
  purpose: 'public_catalog_media',
  visibility: 'public',
  public: true,
  resource: 'service',
});

export default {
  name: "MultimediaTab",
  components: {
    IconCircleX,
    FileUploadDropzone
  },
  props: {
    showDetails: {
      type: Boolean,
      default: false
    },
    service: {
      type: Object,
      default: null
    },
    errors: {
      type: Array,
      default: () => []
    }
  },
  emits: ['photos-changed', 'refresh-photos'],
  data() {
    return {
      mediaItems: [],
      initialPhotoStorageIds: [],
      lastEmittedPhotoStorageIds: null,

      // Upload state
      isUploading: false,
      uploadingFileCount: 0,
      uploadProgress: null, // { overall, current, total }
      uploadErrors: [],
      isRefreshing: false,

      toast: useToast()
    };
  },
  computed: {
    maxMediaItems() {
      return SERVICE_MEDIA_MAX_ITEMS;
    },

    totalPhotoCount() {
      return this.mediaItems.length + this.uploadingFileCount;
    },

    catalogMediaUploadMetadata() {
      const metadata = { ...SERVICE_MEDIA_UPLOAD_METADATA };

      if (this.service?.id !== null && this.service?.id !== undefined) {
        metadata.resource_id = this.service.id;
      }

      return metadata;
    },

    photoStorageIds() {
      return getServicePhotoStorageIds(this.mediaItems);
    },

    allPhotoStorageIds() {
      return this.photoStorageIds;
    }
  },
  watch: {
    service: {
      handler(value) {
        this.resetState();
        if (value) {
          this.loadServicePhotos(value);
        }
      },
      immediate: true,
      deep: true
    },

    photoStorageIds: {
      handler() {
        this.emitPhotosChanged();
      },
      deep: true
    }
  },
  methods: {
    resetState() {
      this.revokeLocalPreviews();
      this.mediaItems = [];
      this.initialPhotoStorageIds = [];
      this.lastEmittedPhotoStorageIds = null;
      this.isUploading = false;
      this.uploadingFileCount = 0;
      this.uploadProgress = null;
      this.uploadErrors = [];
      this.isRefreshing = false;
    },

    loadServicePhotos(service) {
      if (!service) return;

      const photos = normalizeServiceMediaItems(service);
      this.mediaItems = photos;
      this.initialPhotoStorageIds = getServicePhotoStorageIds(photos);
      this.emitPhotosChanged();
    },

    onUploadStart(files) {
      this.isUploading = true;
      this.uploadingFileCount = Array.isArray(files) ? files.length : 0;
      this.uploadErrors = [];
      this.uploadProgress = {
        overall: 0,
        current: 0,
        total: this.uploadingFileCount
      };
    },

    onUploadProgress(_file, progress, summary) {
      if (this.uploadProgress) {
        this.uploadProgress.overall = summary?.overall ?? progress?.progress ?? 0;
        this.uploadProgress.current = (summary?.completedCount ?? 0) + (summary?.failedCount ?? 0);
        this.uploadProgress.total = summary?.total ?? this.uploadProgress.total;
      }
    },

    onUploadComplete(results) {
      const uploadResults = results || { successful: [], failed: [] };
      const uploadedItems = normalizeUploadedMediaItems(uploadResults);

      if (uploadResults.successful && uploadResults.successful.length > 0) {
        const mergeResult = mergeServiceMediaItems(this.mediaItems, uploadedItems, this.maxMediaItems);
        this.mediaItems = mergeResult.items;

        if (mergeResult.skippedItems.length > 0) {
          revokeServiceMediaPreviews(mergeResult.skippedItems);
          this.toast.warning(i18n.global.t("services.auto.algunas_fotos_no_se_agregaron_porque_ya_existian"), {
            timeout: 4000,
            position: 'top-right'
          });
        }

        if (mergeResult.addedItems.length > 0) {
          this.toast.success(
            i18n.global.t("services.media.photos_uploaded", {
              count: mergeResult.addedItems.length,
            }),
            { timeout: 3000, position: 'top-right' }
          );
        }
      }

      // Handle failed uploads
      if (uploadResults.failed && uploadResults.failed.length > 0) {
        const errors = uploadResults.failed.map(failure =>
          `${this.getUploadFileName(failure)}: ${
            failure.error?.message ||
            i18n.global.t("services.media.upload_default_error")
          }`
        );
        this.uploadErrors.push(...errors);

        this.toast.error(
          i18n.global.t("services.media.photos_failed", {
            count: uploadResults.failed.length,
          }),
          { timeout: 5000, position: 'top-right' }
        );
      }

      this.isUploading = false;
      this.uploadProgress = null;
      this.$refs.dropzone?.clearCompletedFiles?.();
      this.uploadingFileCount = uploadResults.failed?.length || 0;
    },

    onUploadError(file, error) {
      const fileName = this.getUploadFileName(file);
      console.error('Upload error for file:', fileName, error);
      this.uploadErrors.push(`${fileName}: ${error.message}`);

      this.toast.error(
        i18n.global.t("services.media.upload_error", {
          fileName,
          message: error.message,
        }),
        { timeout: 5000, position: 'top-right' }
      );
    },

    onFilesChanged(files) {
      this.uploadingFileCount = Array.isArray(files)
        ? files.filter(file => !file.cancelled && !file.storageId).length
        : 0;
    },

    removeMediaItem(index) {
      if (index >= 0 && index < this.mediaItems.length) {
        revokeServiceMediaPreview(this.mediaItems[index]);
        this.mediaItems.splice(index, 1);

        this.toast.info(i18n.global.t("services.auto.foto_eliminada_se_aplicara_al_guardar"), {
          timeout: 3000,
          position: 'top-right'
        });
      }
    },

    removeNewPhoto(index) {
      const uploadedItems = this.mediaItems.filter(item => item.source === 'uploaded');
      const item = uploadedItems[index];
      if (!item) return;

      this.removeMediaItem(this.mediaItems.findIndex(mediaItem => mediaItem.id === item.id));
    },

    handlePhotoError(photo) {
      console.warn('Photo failed to load, possibly expired:', photo.photoUrl);
      if (photo) {
        photo.expired = true;
      }
    },

    async refreshPhotoUrls() {
      if (!this.service?.id) {
        console.warn('Cannot refresh photos: no service ID');
        return;
      }

      this.isRefreshing = true;

      try {
        // Emit event to parent component to refresh service data
        this.$emit('refresh-photos', this.service.id);

        // Reset expired flags
        this.mediaItems.forEach(photo => {
          photo.expired = false;
        });

        this.toast.info(i18n.global.t("services.auto.urls_refrescadas"), {
          timeout: 2000,
          position: 'top-right'
        });
      } catch (error) {
        console.error('Failed to refresh photo URLs:', error);
        this.toast.error(i18n.global.t("services.auto.error_refrescando_imagenes"), {
          timeout: 3000,
          position: 'top-right'
        });
      } finally {
        this.isRefreshing = false;
      }
    },

    clearUploadErrors() {
      this.uploadErrors = [];
    },

    // Public methods for parent components
    getPhotoStorageIds() {
      return this.allPhotoStorageIds;
    },

    getCurrentPhotos() {
      return this.mediaItems
        .filter(item => item.source === 'existing')
        .map(item => this.toLegacyPhoto(item));
    },

    getNewPhotos() {
      return this.mediaItems
        .filter(item => item.source === 'uploaded')
        .map(item => ({
          storageId: item.storageId,
          downloadUrl: item.photoUrl,
          file: item.originalName
        }));
    },

    hasPhotos() {
      return this.mediaItems.length > 0;
    },

    canAddMore() {
      return this.totalPhotoCount < this.maxMediaItems;
    },

    emitPhotosChanged() {
      const ids = this.photoStorageIds;
      const emittedKey = ids.join(',');
      if (this.lastEmittedPhotoStorageIds === emittedKey) {
        return;
      }

      this.lastEmittedPhotoStorageIds = emittedKey;
      this.$emit(
        'photos-changed',
        ids,
        buildServicePhotosChangedPayload(this.mediaItems, this.initialPhotoStorageIds)
      );
    },

    getUploadFileName(fileData) {
      return fileData?.originalName || fileData?.file?.name || fileData?.name || 'archivo';
    },

    toLegacyPhoto(item) {
      return {
        photoUrl: item.photoUrl,
        storage_id: item.storageId,
        expired: item.expired
      };
    },

    revokeLocalPreviews() {
      revokeServiceMediaPreviews(this.mediaItems);
    },

    // Reset component state (useful when creating new service)
    reset() {
      this.resetState();
      if (this.$refs.dropzone) {
        this.$refs.dropzone.clearFiles();
      }
    },

    resetForm() {
      this.reset();
    }
  },

  beforeUnmount() {
    this.revokeLocalPreviews();
  }
};
</script>

<style lang="scss" scoped>
.object-cover {
  object-fit: cover;
}

/* Custom scrollbar for photo containers */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  @apply bg-gray-100 rounded;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  @apply bg-gray-300 rounded;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400;
}
</style>
